import { ArrowUp } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { BackstageStrip } from './BackstageStrip'
import {
  BACKSTAGE,
  COMPLETE,
  COMPOSER_LABEL,
  CURRENT_STEP,
  PRIORITIES,
  STEPS,
  useBackstageRun,
} from './cfoSetup'
import { ChatOptions } from './ChatOptions'
import { CFO_SETUP_THREAD, Speaker } from './contacts'
import { BlackMark } from './FleetTopNav'
import { MessageList } from './MessageList'
import { SignedInAvatar } from './SignedInAvatar'

/**
 * The same flow on a phone. There is no room for the rail beside the chat, so
 * the stepper collapses to the step you are on and the bar above it carries
 * how far that is — the conversation keeps the whole screen.
 */
export function CfoSetupMobile({ className }: { className?: string }) {
  const [priority, setPriority] = useState<string | null>(null)
  const [sent, setSent] = useState(0)
  const { running, exiting } = useBackstageRun(sent)
  const messages = priority ? [...CFO_SETUP_THREAD, { from: Speaker.YOU, text: priority }] : CFO_SETUP_THREAD

  function send(entry: string) {
    setPriority(entry)
    setSent((count) => count + 1)
  }

  return (
    <div className={cn('border-ink-100 flex flex-col overflow-hidden border bg-white', className)}>
      <Progress
        value={COMPLETE}
        aria-label="Setup progress"
        className="bg-ink-50 [&_[data-slot=progress-indicator]]:bg-ink-700 h-1 shrink-0 rounded-none"
      />

      <div className="border-ink-100 flex shrink-0 items-center gap-2 border-b px-3 py-2.5">
        <BlackMark />
        <span className="text-ink truncate text-xs font-semibold">Add CFO capabilities</span>
        <span className="ml-auto">
          <SignedInAvatar />
        </span>
      </div>

      {/* The rail's job in one line: which step, and how many are left. */}
      <div className="flex shrink-0 items-baseline justify-between px-3 pt-4">
        <span className="text-ink text-xs font-semibold">{STEPS[CURRENT_STEP].label}</span>
        <span className="text-ink-300 text-[10px]">
          Step {CURRENT_STEP + 1} of {STEPS.length}
        </span>
      </div>

      <BackstageStrip items={BACKSTAGE} current={running} exiting={exiting} />

      <MessageList
        fadeTop
        animateLast={priority !== null}
        messages={messages}
        trailing={!priority && <ChatOptions options={PRIORITIES} onSelect={send} />}
      />

      <div className="flex shrink-0 items-center px-3 pt-2 pb-3">
        <div className="border-input bg-surface hover:border-ink-300 flex flex-1 items-center gap-2 rounded-2xl border px-3 py-2 transition-colors">
          <span className="text-ink-400 flex-1 truncate text-xs">{COMPOSER_LABEL}</span>
          <Button
            variant="iconOnly"
            aria-label="Send"
            onClick={() => setSent((count) => count + 1)}
            className="bg-brand hover:bg-brand-600 size-6 shrink-0 rounded-full p-0"
          >
            <ArrowUp className="text-ink-900 size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
